import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import {
  DOCUMENT_UPLOAD_MAX_TOTAL_BYTES,
  formatUploadMegabytes,
  totalFileMapBytes,
  uploadLimitErrorMessage,
} from '../../utils/documentUploadLimits';
import './css/DocumentImageUploadModal.css';

function createUploadSessionId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Modal for document template upload image slots.
 * Files are sent via multipart upload (not base64 in JSON).
 */
const DocumentImageUploadModal = ({
  show,
  slots = [],
  onHide,
  onConfirm,
  generating = false,
}) => {
  const [filesByKey, setFilesByKey] = useState({});
  const [previewUrls, setPreviewUrls] = useState({});
  const [fileSizes, setFileSizes] = useState({});
  const [uploadSessionId, setUploadSessionId] = useState('');
  const [error, setError] = useState('');
  const previewUrlsRef = useRef(previewUrls);
  previewUrlsRef.current = previewUrls;

  useEffect(() => {
    if (!show) {
      Object.values(previewUrlsRef.current).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
      setFilesByKey({});
      setPreviewUrls({});
      setFileSizes({});
      setUploadSessionId('');
      setError('');
      return;
    }
    setUploadSessionId(createUploadSessionId());
  }, [show]);

  const totalBytes = useMemo(() => totalFileMapBytes(fileSizes), [fileSizes]);

  const usageLabel = `${formatUploadMegabytes(totalBytes)} / ${formatUploadMegabytes(DOCUMENT_UPLOAD_MAX_TOTAL_BYTES)}`;
  const isNearLimit = totalBytes > DOCUMENT_UPLOAD_MAX_TOTAL_BYTES * 0.85;
  const isOverLimit = totalBytes > DOCUMENT_UPLOAD_MAX_TOTAL_BYTES;

  const requiredSlots = useMemo(
    () => (Array.isArray(slots) ? slots.filter((s) => s.required) : []),
    [slots]
  );

  const handleFileChange = (key, file) => {
    if (!file) {
      setFilesByKey((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setPreviewUrls((prev) => {
        if (prev[key]) URL.revokeObjectURL(prev[key]);
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setFileSizes((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file.');
      return;
    }

    const otherBytes = totalFileMapBytes(fileSizes) - (fileSizes[key] || 0);
    const nextTotal = otherBytes + file.size;
    if (nextTotal > DOCUMENT_UPLOAD_MAX_TOTAL_BYTES) {
      setError(uploadLimitErrorMessage(nextTotal));
      return;
    }

    setError('');
    const objectUrl = URL.createObjectURL(file);
    setFilesByKey((prev) => ({ ...prev, [key]: file }));
    setPreviewUrls((prev) => {
      if (prev[key]) URL.revokeObjectURL(prev[key]);
      return { ...prev, [key]: objectUrl };
    });
    setFileSizes((prev) => ({ ...prev, [key]: file.size }));
  };

  const handleConfirm = () => {
    if (generating) return;

    const missing = requiredSlots.filter((slot) => !filesByKey[slot.key]);
    if (missing.length > 0) {
      const labels = missing.map((s) => s.label || s.key).join(', ');
      setError(`Please upload required image(s): ${labels}`);
      return;
    }

    if (isOverLimit) {
      setError(uploadLimitErrorMessage(totalBytes));
      return;
    }

    const files = {};
    (slots || []).forEach((slot) => {
      if (filesByKey[slot.key]) {
        files[slot.key] = filesByKey[slot.key];
      }
    });

    onConfirm({ files, uploadSessionId });
  };

  const handleHide = () => {
    if (generating) return;
    onHide();
  };

  if (!show) {
    return null;
  }

  return (
    <Modal
      show={show}
      onHide={handleHide}
      centered
      size="lg"
      backdrop={generating ? 'static' : true}
      keyboard={!generating}
      className="document-image-upload-modal"
      container={typeof document !== 'undefined' ? document.body : undefined}
    >
      <Modal.Header closeButton={!generating}>
        <Modal.Title>Document Images</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-2">
          Upload images for this document. If the same image key appears multiple times in the
          template, one upload is used for all of those places.
        </p>
        <p
          className={`small mb-3 ${isOverLimit ? 'text-danger fw-semibold' : isNearLimit ? 'text-warning' : 'text-muted'}`}
        >
          Total upload size: {usageLabel}
          {isOverLimit ? ' — reduce size before generating.' : ''}
        </p>

        {error && (
          <Alert variant="danger" className="py-2" onClose={() => setError('')} dismissible>
            {error}
          </Alert>
        )}

        {(!slots || slots.length === 0) && (
          <Alert variant="info" className="mb-0">
            No upload images required for this template.
          </Alert>
        )}

        {(slots || []).map((slot) => (
          <div key={slot.key} className="border rounded p-3 mb-3">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div>
                <strong>{slot.label || slot.key}</strong>
                {slot.required ? (
                  <span className="text-danger ms-1">*</span>
                ) : (
                  <span className="text-muted small ms-2">(optional)</span>
                )}
                <div className="text-muted small">
                  Key: <code>{slot.key}</code>
                  {slot.occurrences > 1 ? ` · used ${slot.occurrences} times` : ''}
                </div>
              </div>
            </div>

            <Form.Group>
              <Form.Label className="small mb-1">Select image</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                disabled={generating}
                onChange={(e) => handleFileChange(slot.key, e.target.files?.[0] || null)}
              />
            </Form.Group>

            {previewUrls[slot.key] && (
              <div className="mt-2">
                <img
                  src={previewUrls[slot.key]}
                  alt={slot.label || slot.key}
                  style={{
                    maxWidth: '200px',
                    maxHeight: '120px',
                    objectFit: 'contain',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                  }}
                />
                {fileSizes[slot.key] ? (
                  <div className="text-muted small mt-1">
                    {formatUploadMegabytes(fileSizes[slot.key])}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        ))}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleHide} disabled={generating}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleConfirm} disabled={generating || isOverLimit}>
          {generating ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" role="status" aria-hidden="true" />
              Generating...
            </>
          ) : (
            'Generate Document'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DocumentImageUploadModal;
